import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Icon, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconPanorama360Filled, IconPlusCircleFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing,
  useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing,
  useRentalManagerUserPropertyVrModelWebApiHandler_associateListing,
  useRentalManagerUserPropertyVrModelWebApiHandler_associateModel,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { getDefaultTourLabel, getModelId, updateTour } from '../../../../../client/utils/3dToursUtils';
import PostingActions from '../../../../actions/PostingActions';
import PropertiesActions from '../../../../actions/PropertiesActions';
import { useSelector } from '../../../../selectors/useSelector';
import { mapTourTypeDisplayToEnum } from '../../../../utils/tourTypeMapping';

import ErrorMessageConstants from '../../../../constants/ErrorMessageConstants';
import { GET_LISTING_RECS, RECS } from '../../../../constants/RecommendationsConstants';
import ExternalTour from '../../../../model/ExternalTour';

import SelectTourModal from '../../../../components/Modal/SelectTourModal';
import RecommendationsLearnMoreContent from '../../../../components/RecommendationsLearnMoreContent';

import { emptyTourCardContainerStyles } from '../styles';

import type { KeysOfTourType } from '../../../../../client/utils/3dToursUtils';
import type { ExternalTourProps } from '../../../../model/ExternalTour';
import type { VrModelProps } from '../../../../model/VrModel';

type Props = {
  isDisabled?: boolean;
  isHidden?: boolean;
  listingToursCount: number;
  tourType: KeysOfTourType;
  floorPlanIndex?: number;
  unitIndex?: number;
  isRelistingFieldset?: boolean;
};

type handleAssociateArgs = {
  shouldAssociate?: boolean;
  tourId?: string;
  externalTour?: ExternalTourProps;
};

const TourCardEmpty = ({
  floorPlanIndex,
  unitIndex,
  tourType,
  listingToursCount,
  isDisabled,
  isHidden,
  isRelistingFieldset = false,
}: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync: associateVrModel } = useRentalManagerUserPropertyVrModelWebApiHandler_associateModel();
  const { mutateAsync: associateVrModelListing } = useRentalManagerUserPropertyVrModelWebApiHandler_associateListing();
  const { mutateAsync: addExternalTourForListing } =
    useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing',
        },
      },
    });
  const { mutateAsync: removeExternalTourFromListing } =
    useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing',
        },
      },
    });

  const [displaySelectTourModal, setDisplaySelectTourModal] = useState(false);
  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const {
    user: { isMultifamilyCustomer, isSingleFamilyEnterpriseCustomer },
    properties: {
      currentProperty,
      currentProperty: { syndication, isMultifamily, listingAlias },
    },
  } = useSelector((state) => state);

  // @ts-expect-error
  const updateCurrentProperty = (data) => dispatch(PropertiesActions.updateCurrentProperty(data));

  const handleAddOrEditTourClick = () => {
    setDisplaySelectTourModal(true);
  };

  const handleAssociate = ({ shouldAssociate = false, tourId, externalTour }: handleAssociateArgs) => {
    const isFloorPlanorUnit3dTour =
      (floorPlanIndex !== undefined && floorPlanIndex > -1) || (unitIndex !== undefined && unitIndex > -1);

    const apiToCall = isFloorPlanorUnit3dTour ? 'associateModel' : 'associateListing';
    const modelId =
      isFloorPlanorUnit3dTour &&
      getModelId({
        floorPlanIndex,
        currentProperty,
        unitIndex,
      });

    if (tourId) {
      if (isFloorPlanorUnit3dTour) {
        return associateVrModel({
          getParams: {
            vrModelId: Number(tourId),
            listingAlias,
            shouldAssociate: !!shouldAssociate,
            encodedFloorplanKey: (modelId as string) ?? '',
          },
        });
      }
      return associateVrModelListing({
        getParams: { vrModelId: Number(tourId), listingAlias, shouldAssociate: !!shouldAssociate },
      });
    }
    if (externalTour) {
      if (isFloorPlanorUnit3dTour) {
        return dispatch(
          PostingActions.property.externalTour.associateModel(externalTour, listingAlias, shouldAssociate, modelId),
        );
      } else {
        const tourData = {
          label: externalTour.label,
          tourUrl: externalTour.tourUrl,
          sourceName: externalTour.sourceName,
          tourType: mapTourTypeDisplayToEnum(externalTour.tourType),
          published: true,
        };

        if (shouldAssociate) {
          return addExternalTourForListing({
            getParams: { listingAlias },
            postData: tourData,
          }).then((result) => ExternalTour(result));
        } else {
          return removeExternalTourFromListing({
            getParams: { listingAlias },
            postData: tourData,
          }).then(() => ExternalTour());
        }
      }
    }
    return Promise.resolve();
  };

  const handleAdd = (isZillowTour: boolean, newSelectedTourId: string, newExternalTour: ExternalTourProps) => {
    return (
      handleAssociate({
        shouldAssociate: true,
        ...(isZillowTour && { tourId: newSelectedTourId }),
        ...(!isZillowTour && { externalTour: newExternalTour }),
      })
        // @ts-expect-error
        .then((tour: VrModelProps | ExternalTourProps) => {
          updateTour({
            floorPlanIndex: floorPlanIndex as number,
            unitIndex: unitIndex as number,
            currentProperty,
            tourToUpdate: tour,
            tourType,
            updateCurrentProperty,
            isExternalTour: !isZillowTour,
          });
          queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
          setDisplaySelectTourModal(false);
        })
        .catch((error: unknown) => {
          if (isZillowTour) {
            return Promise.reject('Cannot associate this tour, please try again later.');
          } else {
            const errorMessage = DatarouterError.getDisplayMessage(error);
            const lowerCaseMessage = errorMessage.toLowerCase();

            if (lowerCaseMessage.includes('invalid url')) {
              return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL);
            }
            if (lowerCaseMessage.includes('tour already exists')) {
              return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST);
            }
            if (lowerCaseMessage.includes('is not from a supported provider')) {
              return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER);
            }
            if (lowerCaseMessage.includes('doesn’t have permission')) {
              return Promise.reject(
                ErrorMessageConstants.EXTERNAL_TOUR_NO_PERMISSION(
                  isMultifamily && isMultifamilyCustomer,
                  isSingleFamilyEnterpriseCustomer,
                ),
              );
            }
            return Promise.reject('Cannot associate this tour, please try again later.');
          }
        })
    );
  };

  const handleSelectTourSubmit = (
    isZillowTour: boolean,
    newSelectedTourId: string,
    newExternalTour: ExternalTourProps,
  ) => {
    return handleAdd(isZillowTour, newSelectedTourId, newExternalTour);
  };

  const renderContent = () => {
    if (isRelistingFieldset) {
      return (
        <>
          <Text textStyle={isRelistingFieldset ? 'body' : 'body-sm'}>
            Add a 3D tour using the Zillow 3D Home® app or an external link.
          </Text>
          <Box css={{ display: 'flex', flexDirection: 'row', paddingTop: '400' }}>
            <Button
              tone="neutral"
              emphasis="outlined"
              size="sm"
              disabled={isDisabled}
              onClick={handleAddOrEditTourClick}
            >
              Add 3D tour
            </Button>
            <TextButton
              textStyle={isRelistingFieldset ? 'body-bold' : 'body-sm-bold'}
              css={{ paddingLeft: '400' }}
              onClick={() => {
                setDisplayRecsModal(true);
              }}
            >
              Learn more
            </TextButton>
          </Box>
        </>
      );
    }
    return (
      <Box css={{ ...emptyTourCardContainerStyles, maxWidth: listingToursCount === 0 ? '100%' : '270px' }}>
        <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
          <Icon size="md" css={{ color: 'icon.subtle' }} render={<IconPanorama360Filled />} />
        </Box>
        {isHidden ? (
          <Paragraph textStyle={listingToursCount === 0 ? 'body' : 'body-xs'}>
            Floor plans with units only show 3D tours at the unit level. Please add your 3D tour to individual units to
            have 3D tours show on our sites.
          </Paragraph>
        ) : (
          <>
            <Paragraph textStyle={listingToursCount === 0 ? 'body' : 'body-xs'} css={{ marginBottom: 'default' }}>
              Add via Zillow 3D Home app or an external link
            </Paragraph>
            <Button
              size="sm"
              tone="brand"
              emphasis="outlined"
              disabled={isDisabled}
              onClick={handleAddOrEditTourClick}
              data-testid="TourCardEmpty-AddTour"
              icon={<IconPlusCircleFilled />}
            >
              Add tour
            </Button>
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      {renderContent()}
      {displaySelectTourModal && (
        <SelectTourModal
          zillowDisplayStatus={syndication?.zillow?.displayStatus?.toLowerCase()}
          tourType={tourType}
          onSubmit={handleSelectTourSubmit}
          onClose={() => setDisplaySelectTourModal(false)}
          onCancel={() => setDisplaySelectTourModal(false)}
          filterOutVrModelIds={[]}
          initialExternalTour={{
            ...ExternalTour(),
            label: getDefaultTourLabel(tourType, floorPlanIndex, unitIndex, currentProperty),
          }}
        />
      )}
      {isRelistingFieldset && displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={RECS?.VIRTUAL_TOUR}
          isFromRelistingFlow={true}
        />
      )}
    </>
  );
};

export default TourCardEmpty;
